import { useTranslation } from 'react-i18next';
import { Monitor, Moon, Sun } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import { setTheme, type ThemePreference } from '@/Redux/Slices/uiSlice';

const OPTIONS: { value: ThemePreference; icon: LucideIcon; key: string }[] = [
  { value: 'light', icon: Sun, key: 'platform.theme.light' },
  { value: 'dark', icon: Moon, key: 'platform.theme.dark' },
  { value: 'system', icon: Monitor, key: 'platform.theme.system' },
];

/**
 * A three-way segmented control rather than a toggle, because `system` is not
 * the absence of a choice — it is a choice to keep tracking the OS, and a
 * two-state switch has nowhere to show it.
 *
 * Labels are icon-only with an accessible name: this sits in a dense operator
 * header where three words of chrome would outweigh the page beneath it.
 */
export function ThemeSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const preference = useAppSelector((s) => s.ui.theme);

  return (
    <div
      role="radiogroup"
      aria-label={t('platform.theme.label')}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-md border border-[var(--border-strong)] p-0.5',
        className,
      )}
    >
      {OPTIONS.map(({ value, icon: Icon, key }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            title={t(key)}
            onClick={() => dispatch(setTheme(value))}
            className={cn(
              'grid h-6 w-6 place-items-center rounded transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
              active
                ? 'bg-[var(--surface-hover)] text-[var(--foreground)]'
                : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">{t(key)}</span>
          </button>
        );
      })}
    </div>
  );
}
